/**
 * Frontend Configuration
 *
 * Hardcoded values here are fallback defaults only.
 * Live content is fetched from the server on page load via GET /api/content
 * and pushed in real-time via WebSocket whenever an admin saves a change.
 *
 * Pages should read from ConfigContext (useConfig hook) rather than importing
 * this file directly, so they receive live updates without a page reload.
 */

import { io } from 'socket.io-client';

// ─────────────────────────────────────────────────────────────
// API URL helpers
// ─────────────────────────────────────────────────────────────

export function getApiBaseUrl() {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  return `${window.location.protocol}//${window.location.hostname}:5000`;
}

export function getImageUrl(imageUrl) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
  if (imageUrl.startsWith('/api/')) return `${getApiBaseUrl()}${imageUrl}`;
  return imageUrl;
}

// ─────────────────────────────────────────────────────────────
// Hardcoded defaults — used when the server is unreachable
// and as the base that applyAdminConfig merges onto.
// Every field that any page or admin panel reads must exist here.
// ─────────────────────────────────────────────────────────────

const config = {
  security: {
    passwordProtection: true,
  },

  branding: {
    zslName: 'Pryme',
    fslName: 'Apex',
  },

  navbar: {
    productNames: { primary: 'Pryme', secondary: 'Apex' },
    products:  { enabled: true, items: { productNames: true, platforms: true, whyTytonvoice: true } },
    partners:  { enabled: true,  url: 'https://think-a-move.com/alliances-and-partnerships/' },
    aboutUs:   { enabled: true,  url: 'https://think-a-move.com/company-profile/' },
    contactUs: { enabled: true,  url: 'https://think-a-move.com/contact-us/' },
    pricing:   { enabled: false },
    resources: { enabled: false, items: { documentation: false, blog: false, support: false } },
    logoPath:  '',
  },

  // ── Landing page ───────────────────────────────────────────

  landingPage: {
    hero: true, problem: true, products: true, principles: true,
    useCases: true, deployment: true, codeWindow: true,
    features: true, platforms: true, testimonials: false, cta: true,
  },

  landingPageContent: {
    heroTitle:    'Keyword Detection Without Training Data',
    heroSubtitle: 'Recognize any word or phrase instantly — no audio samples required. Deploy smarter speech recognition at scale.',
    heroImage:    null,

    heroStats: [
      { value: '0',        label: 'Training samples required' },
      { value: '<50ms',    label: 'Detection latency' },
      { value: 'Same day', label: 'Time to deploy' },
    ],

    problemText: 'Building voice-enabled products usually means collecting hundreds of audio samples, training custom models, and waiting weeks before you can test anything. Every vocabulary change triggers the whole cycle again. We reduce that to nothing.',

    prymeCardTagline: 'Detect any spoken keyword or phrase the moment you define it — powered by a multi-stage detection engine that understands meaning, not just sound.',
    prymeCardFeatures: [
      'No training examples — text label only',
      'Multi-stage proprietary detection engine',
      'Neural VAD + open transcription mode',
      'Live microphone & audio file support',
      'Edge hardware or cloud deployment',
    ],

    apexCardTagline: 'Bring higher accuracy to speech detection and transcription with a small set of audio examples. Define your terms, provide samples, and deploy the same day.',
    apexCardFeatures: [
      'A small set of audio examples per term',
      'Calibrated multi-stage detection engine',
      'Custom wake words and domain transcription',
      'Full correction log per result',
      'CPU edge or GPU cloud deployment',
    ],

    principles: [
      { label: 'No Training Data Required', detail: 'Define keywords or vocabulary in plain text. No audio samples. No annotation pipeline. Detection or transcription is live from the first utterance.' },
      { label: 'Edge + Cloud',              detail: 'The same pipeline runs on embedded hardware, air-gapped devices, and cloud infrastructure. Same API, same accuracy, wherever you need it.' },
      { label: 'Deploy the Same Day',       detail: 'No retraining cycle, no redeployment. Add or change keywords and vocabulary at runtime. Go from definition to live detection in minutes.' },
    ],

    useCases: [
      { industry: 'Smart Devices & IoT',   product: 'Pryme', description: 'Wake word and voice command detection on embedded hardware — no audio samples, no vendor lock-in, deploy the same day.' },
      { industry: 'Defense & Government',  product: 'Apex',  description: 'Accurate detection of callsigns, operational terms, and unit names in air-gapped environments with no audio leaving the device.' },
      { industry: 'Healthcare',            product: 'Apex',  description: 'Drug names, procedure codes, and specialist terminology captured correctly — configured at runtime, not retrained.' },
      { industry: 'Robotics & Automation', product: 'Pryme', description: 'Natural voice commands — start, stop, and action triggers — with multi-keyword monitoring and noise-robust detection.' },
      { industry: 'Enterprise & Legal',    product: 'Apex',  description: 'Proprietary product names, legal terms, and internal jargon detected and transcribed exactly as defined — every time.' },
      { industry: 'Accessibility',         product: 'Pryme', description: 'Voice interfaces that work immediately for diverse speakers, accents, and speech patterns — no per-user samples required.' },
    ],

    deployOnDeviceTagline: 'Run the complete pipeline on embedded hardware, air-gapped systems, or any CPU-only environment — with no network dependency and no audio leaving the device.',
    deployOnDeviceFeatures: [
      'CPU-optimized inference — no GPU required',
      'Air-gapped and fully offline capable',
      'Low memory footprint for embedded hardware',
      'On-Chip, Linux, Android, iOS, Windows',
      'Full pipeline: VAD · detection · transcription',
    ],

    deployCloudTagline: 'Scale to GPU-backed infrastructure for higher throughput and maximum accuracy. Built for production workloads — concurrent streams, batch processing, and larger models.',
    deployCloudFeatures: [
      'GPU-accelerated inference',
      'Larger, higher-accuracy models',
      'Native batch and concurrent stream support',
      'Same vocabulary and detection API as on-device',
      'You control the infrastructure',
    ],

    codeSectionHeading:    'Up and running in minutes',
    codeSectionSubheading: 'Install the SDK, define your keywords in plain text, and start detecting. No training pipeline. No redeployment cycle.',

    ctaChecklist: [
      'No training examples required',
      'Keyword detection & domain transcription',
      'Edge, on-chip, or cloud deployment',
      'Runtime vocabulary — no redeployment',
      'Deploy the same day',
    ],

    // Legacy fields kept for backward compatibility
    features: [
      { title: 'No Training Data',    description: 'Define keywords in plain text and start detecting immediately. No samples, no pipeline, no wait.',    image: null },
      { title: 'Run Anywhere',        description: 'From embedded chips to cloud servers. Deploy on any device, any platform.',                           image: null },
      { title: 'Deploy the Same Day', description: 'From keyword definition to live detection in minutes. Runtime vocabulary changes, no redeployment.', image: null },
    ],
    cta: {
      title:       'See It In Action',
      description: 'Try our live demo. Define keywords, speak naturally, watch instant recognition happen.',
    },
  },

  // ── Pryme page ─────────────────────────────────────────────

  prymePage: {
    heroTitle:    'Keyword Detection.\nNo Training Required.',
    heroSubtitle: 'Detect any spoken keyword or phrase the moment you define it in plain text. No audio samples, no training pipeline, no redeployment cycle.',

    heroStats: [
      { value: '0',     label: 'Training samples required' },
      { value: '<50ms', label: 'Detection latency' },
      { value: '3',     label: 'Verification layers' },
    ],

    pipeline: [
      { summary: 'Neural VAD continuously monitors the audio stream, identifying genuine speech and discarding silence before any processing begins.' },
      { summary: 'Whisper transcribes each detected speech segment into text, providing the transcript that the detection engine evaluates.' },
      { summary: "Pryme's proprietary detection engine evaluates the transcript against every target keyword simultaneously — identifying matches across natural speech variation." },
      { summary: 'A second scoring pass encodes the transcript to assess how closely it relates to each target label — catching paraphrases and command variants.' },
      { summary: 'Both scores are combined into a single confidence value. If it clears the configured threshold, a detection event fires.' },
    ],

    capabilities: [
      { title: 'No Training Data',              icon: '/icons/pryme/training-data.png',      desc: 'Define any keyword or phrase in plain text and start detecting immediately. No audio samples, no annotation pipeline, no labelling effort.' },
      { title: 'Multi-Stage Detection',         icon: '/icons/pryme/detection.png',          desc: "A proprietary multi-stage engine evaluates each transcript against your keyword list — handling natural speech variation, synonyms, and paraphrases natively." },
      { title: 'Neural Voice Activity Detection', icon: '/icons/pryme/neural-vad.png',       desc: 'A dedicated neural VAD layer filters silence and non-speech audio before detection begins, keeping compute usage low and false positives minimal.' },
      { title: 'Open Transcription Mode',       icon: '/icons/pryme/open-transcription.png', desc: 'Run Pryme in open transcription mode to capture a full speech-to-text transcript alongside keyword detection — no separate ASR integration needed.' },
      { title: 'Live Mic & Audio File',         icon: '/icons/pryme/mic-audio-file.png',     desc: 'Process live microphone input or pre-recorded audio files. Both modes use the same detection pipeline and return the same structured result.' },
      { title: 'Edge & Cloud Deployment',       icon: '/icons/pryme/edge-cloud.png',         desc: 'Deploy on embedded hardware, air-gapped systems, or cloud infrastructure. The same SDK, the same API, the same accuracy wherever your deployment requires.' },
    ],

    advantages: [
      { value: '0',       metric: 'Training Examples Required', desc: 'Define any keyword in plain text and start detecting immediately' },
      { value: '3-Layer', metric: 'Verification Architecture',  desc: 'Intent · Semantic · Precision Guard' },
      { value: '<50ms',   metric: 'Detection Latency',          desc: 'Low-latency pipeline on edge hardware and cloud deployments' },
      { value: 'Live',    metric: 'Runtime Customization',      desc: 'Add or change keywords instantly, no redeployment' },
    ],

    useCases: [
      { title: 'Smart Devices & IoT',        desc: 'Wake word and voice command detection on embedded hardware — define keywords in text and deploy same day with no audio samples.', features: ['No training data', 'Edge-ready', 'Deploy same day'] },
      { title: 'Robotics & Automation',      desc: 'Voice-commanded systems with multi-keyword monitoring. Start, stop, and action triggers with noise-robust detection on CPU hardware.', features: ['Multi-keyword', 'Noise robust', 'CPU inference'] },
      { title: 'Accessibility',              desc: 'Voice interfaces that work immediately for diverse speakers, accents, and speech patterns — no per-user audio samples required.', features: ['Any speaker', 'No samples', 'Instant setup'] },
      { title: 'Enterprise Voice Interfaces',desc: 'Internal command vocabularies, product names, and operational terms — defined in text, deployed the same day, updated at runtime.', features: ['Runtime updates', 'No redeployment', 'Edge or cloud'] },
    ],

    ctaBody: 'Define your keywords in plain text and start detecting immediately — on embedded hardware, in the cloud, or anywhere in between.',
    ctaChecklist: [
      'Zero audio samples — text label only',
      'Multi-stage proprietary detection engine',
      'Edge hardware or cloud deployment',
      'Live microphone and audio file support',
      'Deploy the same day',
    ],

    sections: {
      hero: true, pipeline: true, capabilities: true,
      advantages: true, useCases: true, comparison: true, privacy: true,
    },
  },

  // ── Apex page ──────────────────────────────────────────────

  apexPage: {
    heroTitle:    'Keyword Detection.\nCalibrated to\nYour Vocabulary.',
    heroSubtitle: 'Apex is the precision step up from Pryme. Provide a small set of audio examples and Apex calibrates its multi-stage detection engine to your exact keywords, wake words, and domain vocabulary.',

    heroStats: [
      { value: 'Small set', label: 'Audio examples per term' },
      { value: 'Minutes',   label: 'Calibration time, runs locally' },
      { value: 'Higher',    label: 'Accuracy vs generic systems' },
    ],

    insightPara1: "Pryme detects any keyword the moment you define it in text — no audio samples required. That's the right tool when speed of deployment matters most and vocabulary changes frequently. But for stable, known vocabularies — brand wake words, operational callsigns, clinical terminology — there's a higher accuracy ceiling available.",
    insightPara2: "Apex reaches that ceiling. Provide a small set of audio recordings per term and Apex calibrates its detection engine to your exact vocabulary — in your speakers' voices, in your acoustic environment. The result is higher detection confidence, lower false-positive rates, and accurate transcription of domain terms that general-purpose ASR engines consistently mis-render.",

    pipeline: [
      { summary: 'Provide a small number of short audio recordings of each target term. These are the only inputs required — no annotation, no large dataset, no labelling pipeline.' },
      { summary: 'Apex processes your audio examples and calibrates its detection engine for your target vocabulary, producing a deployment-ready configuration in minutes.' },
      { summary: 'The same neural VAD layer as Pryme continuously monitors the audio stream, identifying speech boundaries before inference begins.' },
      { summary: "Each incoming speech segment is evaluated by Apex's multi-stage detection engine against your calibrated vocabulary, returning a confidence result per term." },
      { summary: 'Apex returns per-term confidence scores, the top matching label, a triggered flag, and the speech transcript — with word-level timestamps on transcription results.' },
    ],

    modeNoSample: [
      'No audio samples required',
      'Instant — define and deploy same day',
      'Live label changes at runtime',
      'Multi-stage detection engine',
    ],

    modeCalibrated: [
      'A small set of audio examples per term',
      'Calibrates locally in minutes',
      'Higher accuracy on known vocabulary',
      'Custom wake words, domain transcription',
    ],

    capabilities: [
      { title: 'Adaptive Keyword Learning',       icon: '/icons/apex/adaptive-keyword.png',  desc: 'Provide a small number of audio examples per term and Apex calibrates its detection engine for that vocabulary — delivering higher accuracy on the terms that matter most.' },
      { title: 'Custom Wake Word Detection',      icon: '/icons/apex/custom-wake.png',       desc: 'Calibrate Apex to your exact wake word with a small set of audio examples — delivering precision that generic voice systems cannot match.' },
      { title: 'Keyword Detection',               icon: '/icons/apex/keyword.png',           desc: 'Keyword detection with a higher accuracy ceiling for stable, known vocabularies — providing greater confidence across varied speakers and acoustic conditions.' },
      { title: 'Domain Vocabulary Transcription', icon: '/icons/apex/domain-vocab.png',      desc: 'Open-ended transcription calibrated for domain-specific terms. Provide audio examples and Apex renders them correctly where general-purpose ASR consistently fails.' },
      { title: 'Edge & Cloud Ready',              icon: '/icons/apex/edge-cloud.png',        desc: 'Deploy on embedded hardware, air-gapped systems, or GPU-backed cloud infrastructure. Same SDK, same API, same accuracy wherever your deployment requires.' },
      { title: 'Separate SDK',                    icon: '/icons/apex/sdk.png',               desc: 'Apex ships as a standalone SDK with its own install and class structure, sharing the same audio pipeline and VAD layer as Pryme.' },
    ],

    advantages: [
      { value: 'Small set',  metric: 'Audio Examples Per Term',      desc: 'All Apex needs to calibrate to a keyword, wake word, or domain term' },
      { value: 'Higher',     metric: 'Accuracy on Known Vocabulary',  desc: 'Calibrated detection outperforms generic voice systems on stable, defined vocabularies' },
      { value: 'Minutes',    metric: 'Local Calibration Time',        desc: 'Vocabulary calibration completes on your hardware — no cloud pipeline, no waiting' },
      { value: 'Edge→Cloud', metric: 'Deployment Flexibility',        desc: 'Same SDK and API on embedded CPU or full-scale GPU infrastructure' },
    ],

    useCases: [
      { title: 'Defense & Government',  desc: 'Learn specific callsigns, unit designators, and operational commands from a small set of recordings — in air-gapped environments, with no audio transmitted externally.', features: ['Small set of samples', 'Air-gapped deployment', 'On-chip inference'] },
      { title: 'Smart Devices & IoT',   desc: 'Calibrate Apex to your exact wake word with a small set of audio examples — achieving detection precision that generic voice systems cannot match on embedded hardware.', features: ['Custom wake word', 'Edge-ready', 'No vendor lock-in'] },
      { title: 'Healthcare',            desc: 'Provide a few recordings of drug names and specialist terms. Apex calibrates to their profile and transcribes them correctly — without sending audio to a cloud provider.', features: ['Domain vocabulary', 'No audio retained', 'Runtime model loading'] },
      { title: 'Robotics & Automation', desc: 'Calibrate to the exact command vocabulary your system uses. Apex delivers high detection confidence on known commands — critical for safety-sensitive environments.', features: ['High-confidence detection', 'Noise robust', 'Compact model size'] },
    ],

    ctaBody: 'Provide a small set of audio examples per term and deploy calibrated keyword detection, custom wake words, and domain transcription — on embedded hardware or at cloud scale — in minutes.',
    ctaChecklist: [
      "A small set of audio examples — all that's required",
      'Calibrates locally in minutes, no cloud pipeline',
      'Custom wake words with calibrated precision',
      'Keyword detection, wake words & transcription',
      'Edge hardware or cloud GPU deployment',
    ],

    sections: {
      hero: true, insight: true, pipeline: true, capabilities: true,
      advantages: true, useCases: true, comparison: true, privacy: true,
    },
  },

  // ── Demo page ──────────────────────────────────────────────

  demoPage: {
    demoNav: true, demoHeader: true, configPanel: true,
    audioRecorder: true, detectionResults: true, transcriptionDemo: true,
    descriptions: {
      detect:     'Recognize custom keywords in real-time with precision',
      transcribe: 'Instantly convert speech to text with accuracy and speed',
    },
    detectionSettings: {
      lexical_guard:   true,
      fused_threshold: 0.60,
      w_nli:           0.60,
    },
  },

  // ── Footer ─────────────────────────────────────────────────

  footer: {
    enabled:  true,
    logo:     true,
    logoLink: 'https://think-a-move.com/',
    address:  '4807 Rockside Road, Suite 320, Independence, OH 44131',
    socialLinks: {
      linkedin: 'https://www.linkedin.com/company/think-a-move',
      github:   'https://github.com/Think-A-Move',
    },
    sections: {
      product: { enabled: true,  items: { pryme: true, apex: true, liveDemo: true } },
      company: { enabled: true,  items: { aboutUs: true, contact: true } },
      legal:   { enabled: true,  items: { privacy: true, terms: true, accessibility: true } },
    },
    copyright: true,
  },

  fontSizes: {
    navbar: {
      logo: '24px', link: '20px', dropdownTitle: '24px', dropdownItem: '20px', ctaButton: '16px',
    },
    landingPage: {
      heroTitle: '48px', heroSubtitle: '20px', sectionHeader: '36px',
      featureTitle: '20px', featureDescription: '16px',
      platformName: '18px', platformDescription: '14px',
      testimonialQuote: '18px', testimonialInfo: '14px',
      ctaTitle: '32px', ctaDescription: '16px', ctaButton: '16px',
    },
    demoPage: {
      demoNavButton: '16px', demoHeader: '20px', sectionTitle: '28px',
      label: '14px', bodyText: '16px', button: '16px',
    },
    footer: {
      logo: '20px', sectionTitle: '16px', link: '14px', copyright: '12px',
    },
  },
};

export default config;

// ─────────────────────────────────────────────────────────────
// Apply a flat admin config object onto the default config tree.
// Returns a new merged config — does not mutate the original.
//
// Uses `!== undefined` checks (not truthy) so that admins can
// save empty strings and zero values without them being silently dropped.
// ─────────────────────────────────────────────────────────────

export function applyAdminConfig(base, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return base;

  const c = JSON.parse(JSON.stringify(base));
  const s = overrides;

  const set = (val, ...path) => {
    if (val === undefined) return;
    let obj = c;
    for (let i = 0; i < path.length - 1; i++) {
      if (obj[path[i]] === undefined) obj[path[i]] = {};
      obj = obj[path[i]];
    }
    obj[path[path.length - 1]] = val;
  };

  // ── Branding ───────────────────────────────────────────────
  if (s.prymeName !== undefined) { set(s.prymeName, 'branding', 'zslName'); set(s.prymeName, 'navbar', 'productNames', 'primary'); }
  if (s.apexName  !== undefined) { set(s.apexName,  'branding', 'fslName'); set(s.apexName,  'navbar', 'productNames', 'secondary'); }
  // Legacy keys
  if (s.zslName   !== undefined) { set(s.zslName, 'branding', 'zslName'); set(s.zslName, 'navbar', 'productNames', 'primary'); }
  if (s.fslName   !== undefined) { set(s.fslName, 'branding', 'fslName'); set(s.fslName, 'navbar', 'productNames', 'secondary'); }

  if (s.navbarLogoPath !== undefined) set(s.navbarLogoPath, 'navbar', 'logoPath');

  // ── Landing — hero ─────────────────────────────────────────
  set(s.heroTitle,    'landingPageContent', 'heroTitle');
  set(s.heroSubtitle, 'landingPageContent', 'heroSubtitle');
  set(s.heroImage,    'landingPageContent', 'heroImage');

  // ── Product page visuals ──────────────────────────────────
  if (!c.prymePage) c.prymePage = {};
  if (s.prymeHeroImage     !== undefined) c.prymePage.heroImage      = s.prymeHeroImage;
  if (s.prymeComparisonImage !== undefined) c.prymePage.comparisonImage = s.prymeComparisonImage;
  if (s.prymePipelineImage  !== undefined) c.prymePage.pipelineImage  = s.prymePipelineImage;

  if (!c.apexPage) c.apexPage = {};
  if (s.apexHeroImage      !== undefined) c.apexPage.heroImage       = s.apexHeroImage;
  if (s.apexComparisonImage  !== undefined) c.apexPage.comparisonImage  = s.apexComparisonImage;
  if (s.apexPipelineImage   !== undefined) c.apexPage.pipelineImage   = s.apexPipelineImage;

  if (s.heroStat1Value !== undefined || s.heroStat1Label !== undefined) {
    c.landingPageContent.heroStats = [
      { value: s.heroStat1Value ?? c.landingPageContent.heroStats[0].value, label: s.heroStat1Label ?? c.landingPageContent.heroStats[0].label },
      { value: s.heroStat2Value ?? c.landingPageContent.heroStats[1].value, label: s.heroStat2Label ?? c.landingPageContent.heroStats[1].label },
      { value: s.heroStat3Value ?? c.landingPageContent.heroStats[2].value, label: s.heroStat3Label ?? c.landingPageContent.heroStats[2].label },
    ];
  }

  // ── Landing — content sections ─────────────────────────────
  set(s.problemText,      'landingPageContent', 'problemText');
  set(s.prymeCardTagline, 'landingPageContent', 'prymeCardTagline');
  set(s.apexCardTagline,  'landingPageContent', 'apexCardTagline');

  const prymeFeatures = [s.prymeCardFeature1, s.prymeCardFeature2, s.prymeCardFeature3, s.prymeCardFeature4, s.prymeCardFeature5].filter(v => v !== undefined);
  if (prymeFeatures.length) c.landingPageContent.prymeCardFeatures = prymeFeatures;

  const apexFeatures = [s.apexCardFeature1, s.apexCardFeature2, s.apexCardFeature3, s.apexCardFeature4, s.apexCardFeature5].filter(v => v !== undefined);
  if (apexFeatures.length) c.landingPageContent.apexCardFeatures = apexFeatures;

  const principles = [1,2,3].map(n => {
    const existing = c.landingPageContent.principles[n - 1] ?? {};
    return {
      label:  s[`principle${n}Label`]  !== undefined ? s[`principle${n}Label`]  : existing.label,
      detail: s[`principle${n}Detail`] !== undefined ? s[`principle${n}Detail`] : existing.detail,
    };
  });
  c.landingPageContent.principles = principles;

  const ucLanding = [1,2,3,4,5,6].map(n => {
    const existing = c.landingPageContent.useCases[n - 1] ?? {};
    return {
      industry:    s[`ucLanding${n}Industry`] !== undefined ? s[`ucLanding${n}Industry`] : existing.industry,
      product:     s[`ucLanding${n}Product`]  !== undefined ? s[`ucLanding${n}Product`]  : existing.product,
      description: s[`ucLanding${n}Desc`]     !== undefined ? s[`ucLanding${n}Desc`]     : existing.description,
    };
  });
  c.landingPageContent.useCases = ucLanding;

  set(s.deployOnDeviceTagline, 'landingPageContent', 'deployOnDeviceTagline');
  set(s.deployCloudTagline,    'landingPageContent', 'deployCloudTagline');

  const odFeatures = [s.deployOnDeviceFeature1, s.deployOnDeviceFeature2, s.deployOnDeviceFeature3, s.deployOnDeviceFeature4, s.deployOnDeviceFeature5].filter(v => v !== undefined);
  if (odFeatures.length) c.landingPageContent.deployOnDeviceFeatures = odFeatures;

  const cloudFeatures = [s.deployCloudFeature1, s.deployCloudFeature2, s.deployCloudFeature3, s.deployCloudFeature4, s.deployCloudFeature5].filter(v => v !== undefined);
  if (cloudFeatures.length) c.landingPageContent.deployCloudFeatures = cloudFeatures;

  set(s.codeSectionHeading,    'landingPageContent', 'codeSectionHeading');
  set(s.codeSectionSubheading, 'landingPageContent', 'codeSectionSubheading');

  const landingCTA = [s.landingCTACheck1, s.landingCTACheck2, s.landingCTACheck3, s.landingCTACheck4, s.landingCTACheck5].filter(v => v !== undefined);
  if (landingCTA.length) c.landingPageContent.ctaChecklist = landingCTA;

  // ── Landing — section visibility ───────────────────────────
  const landingVis = {
    landingHeroEnabled: 'hero', landingProblemEnabled: 'problem', landingProductsEnabled: 'products',
    landingPrinciplesEnabled: 'principles', landingUseCasesEnabled: 'useCases',
    landingDeploymentEnabled: 'deployment', landingCodeWindowEnabled: 'codeWindow',
    landingCtaEnabled: 'cta', landingShowPlaceholders: 'showPlaceholders',
  };
  Object.entries(landingVis).forEach(([key, prop]) => {
    if (typeof s[key] === 'boolean') c.landingPage[prop] = s[key];
  });

  // ── Pryme page ─────────────────────────────────────────────
  set(s.prymeHeroTitle,    'prymePage', 'heroTitle');
  set(s.prymeHeroSubtitle, 'prymePage', 'heroSubtitle');

  if (s.prymeHeroStat1Value !== undefined || s.prymeHeroStat1Label !== undefined) {
    c.prymePage.heroStats = [
      { value: s.prymeHeroStat1Value ?? c.prymePage.heroStats[0].value, label: s.prymeHeroStat1Label ?? c.prymePage.heroStats[0].label },
      { value: s.prymeHeroStat2Value ?? c.prymePage.heroStats[1].value, label: s.prymeHeroStat2Label ?? c.prymePage.heroStats[1].label },
      { value: s.prymeHeroStat3Value ?? c.prymePage.heroStats[2].value, label: s.prymeHeroStat3Label ?? c.prymePage.heroStats[2].label },
    ];
  }

  const prymePipeline = [1,2,3,4,5].map(n => ({
    summary: s[`prymePipeline${n}Summary`] !== undefined ? s[`prymePipeline${n}Summary`] : c.prymePage.pipeline[n - 1].summary,
  }));
  c.prymePage.pipeline = prymePipeline;

  const prymeCaps = [1,2,3,4,5,6].map(n => {
    const existing = c.prymePage.capabilities[n - 1] ?? {};
    return {
      title: s[`prymeCap${n}Title`] !== undefined ? s[`prymeCap${n}Title`] : existing.title,
      desc:  s[`prymeCap${n}Desc`]  !== undefined ? s[`prymeCap${n}Desc`]  : existing.desc,
      icon:  existing.icon ?? null,
    };
  });
  c.prymePage.capabilities = prymeCaps;

  const prymeAdv = [1,2,3,4].map(n => {
    const existing = c.prymePage.advantages[n - 1] ?? {};
    return {
      value:  s[`prymeAdv${n}Value`]  !== undefined ? s[`prymeAdv${n}Value`]  : existing.value,
      metric: s[`prymeAdv${n}Metric`] !== undefined ? s[`prymeAdv${n}Metric`] : existing.metric,
      desc:   s[`prymeAdv${n}Desc`]   !== undefined ? s[`prymeAdv${n}Desc`]   : existing.desc,
    };
  });
  c.prymePage.advantages = prymeAdv;

  const prymeUC = [1,2,3].map(n => {
    const existing = c.prymePage.useCases[n - 1] ?? {};
    return {
      title:    s[`prymeUC${n}Title`]    !== undefined ? s[`prymeUC${n}Title`]    : existing.title,
      desc:     s[`prymeUC${n}Desc`]     !== undefined ? s[`prymeUC${n}Desc`]     : existing.desc,
      features: [s[`prymeUC${n}Feature1`], s[`prymeUC${n}Feature2`], s[`prymeUC${n}Feature3`]].filter(v => v !== undefined).length
        ? [s[`prymeUC${n}Feature1`] ?? existing.features?.[0], s[`prymeUC${n}Feature2`] ?? existing.features?.[1], s[`prymeUC${n}Feature3`] ?? existing.features?.[2]].filter(Boolean)
        : existing.features ?? [],
    };
  });
  c.prymePage.useCases = prymeUC;

  set(s.prymeCTABody, 'prymePage', 'ctaBody');

  const prymeCTAChecklist = [s.prymeCTACheck1, s.prymeCTACheck2, s.prymeCTACheck3, s.prymeCTACheck4, s.prymeCTACheck5].filter(v => v !== undefined);
  if (prymeCTAChecklist.length) c.prymePage.ctaChecklist = prymeCTAChecklist;

  const prymeVis = {
    prymeHeroEnabled: 'hero', prymePipelineEnabled: 'pipeline', prymeCapabilitiesEnabled: 'capabilities',
    prymeAdvantagesEnabled: 'advantages', prymeUseCasesEnabled: 'useCases',
    prymeComparisonEnabled: 'comparison', prymePrivacyEnabled: 'privacy',
  };
  Object.entries(prymeVis).forEach(([key, prop]) => {
    if (typeof s[key] === 'boolean') c.prymePage.sections[prop] = s[key];
  });
  if (typeof s.prymeShowPlaceholders === 'boolean') c.prymePage.showPlaceholders = s.prymeShowPlaceholders;

  // ── Apex page ──────────────────────────────────────────────
  set(s.apexHeroTitle,    'apexPage', 'heroTitle');
  set(s.apexHeroSubtitle, 'apexPage', 'heroSubtitle');

  if (s.apexHeroStat1Value !== undefined || s.apexHeroStat1Label !== undefined) {
    c.apexPage.heroStats = [
      { value: s.apexHeroStat1Value ?? c.apexPage.heroStats[0].value, label: s.apexHeroStat1Label ?? c.apexPage.heroStats[0].label },
      { value: s.apexHeroStat2Value ?? c.apexPage.heroStats[1].value, label: s.apexHeroStat2Label ?? c.apexPage.heroStats[1].label },
      { value: s.apexHeroStat3Value ?? c.apexPage.heroStats[2].value, label: s.apexHeroStat3Label ?? c.apexPage.heroStats[2].label },
    ];
  }

  set(s.apexInsightPara1, 'apexPage', 'insightPara1');
  set(s.apexInsightPara2, 'apexPage', 'insightPara2');

  const apexPipeline = [1,2,3,4,5].map(n => {
    const existingSummary = s[`apexPipeline${n}Summary`] !== undefined ? s[`apexPipeline${n}Summary`] : (c.apexPage.pipeline[n - 1]?.summary ?? '');
    const existingDetail  = s[`apexPipeline${n}Detail`]  !== undefined ? s[`apexPipeline${n}Detail`]  : (c.apexPage.pipeline[n - 1]?.detail  ?? '');
    return {
      summary: existingSummary,
      detail:  existingDetail || existingSummary,
      label:   s[`apexPipeline${n}Label`] !== undefined ? s[`apexPipeline${n}Label`] : (c.apexPage.pipeline[n - 1]?.label ?? ''),
    };
  });
  c.apexPage.pipeline = apexPipeline;

  const apexModeNoSample = [s.apexModeNoSample1, s.apexModeNoSample2, s.apexModeNoSample3, s.apexModeNoSample4].filter(v => v !== undefined);
  if (apexModeNoSample.length) c.apexPage.modeNoSample = apexModeNoSample;

  const apexModeCalib = [s.apexModeCalib1, s.apexModeCalib2, s.apexModeCalib3, s.apexModeCalib4].filter(v => v !== undefined);
  if (apexModeCalib.length) c.apexPage.modeCalibrated = apexModeCalib;

  const apexCaps = [1,2,3,4,5,6].map(n => {
    const existing = c.apexPage.capabilities[n - 1] ?? {};
    return {
      title: s[`apexCap${n}Title`] !== undefined ? s[`apexCap${n}Title`] : existing.title,
      desc:  s[`apexCap${n}Desc`]  !== undefined ? s[`apexCap${n}Desc`]  : existing.desc,
      icon:  existing.icon ?? null,
    };
  });
  c.apexPage.capabilities = apexCaps;

  const apexAdv = [1,2,3,4].map(n => {
    const existing = c.apexPage.advantages[n - 1] ?? {};
    return {
      value:  s[`apexAdv${n}Value`]  !== undefined ? s[`apexAdv${n}Value`]  : existing.value,
      metric: s[`apexAdv${n}Metric`] !== undefined ? s[`apexAdv${n}Metric`] : existing.metric,
      desc:   s[`apexAdv${n}Desc`]   !== undefined ? s[`apexAdv${n}Desc`]   : existing.desc,
    };
  });
  c.apexPage.advantages = apexAdv;

  const apexUC = [1,2,3].map(n => {
    const existing = c.apexPage.useCases[n - 1] ?? {};
    return {
      title:    s[`apexUC${n}Title`]    !== undefined ? s[`apexUC${n}Title`]    : existing.title,
      desc:     s[`apexUC${n}Desc`]     !== undefined ? s[`apexUC${n}Desc`]     : existing.desc,
      features: [s[`apexUC${n}Feature1`], s[`apexUC${n}Feature2`], s[`apexUC${n}Feature3`]].filter(v => v !== undefined).length
        ? [s[`apexUC${n}Feature1`] ?? existing.features?.[0], s[`apexUC${n}Feature2`] ?? existing.features?.[1], s[`apexUC${n}Feature3`] ?? existing.features?.[2]].filter(Boolean)
        : existing.features ?? [],
    };
  });
  c.apexPage.useCases = apexUC;

  set(s.apexCTABody, 'apexPage', 'ctaBody');

  const apexCTAChecklist = [s.apexCTACheck1, s.apexCTACheck2, s.apexCTACheck3, s.apexCTACheck4, s.apexCTACheck5].filter(v => v !== undefined);
  if (apexCTAChecklist.length) c.apexPage.ctaChecklist = apexCTAChecklist;

  const apexVis = {
    apexHeroEnabled: 'hero', apexInsightEnabled: 'insight', apexPipelineEnabled: 'pipeline',
    apexCapabilitiesEnabled: 'capabilities', apexAdvantagesEnabled: 'advantages',
    apexUseCasesEnabled: 'useCases', apexComparisonEnabled: 'comparison', apexPrivacyEnabled: 'privacy',
  };
  Object.entries(apexVis).forEach(([key, prop]) => {
    if (typeof s[key] === 'boolean') c.apexPage.sections[prop] = s[key];
  });
  if (typeof s.apexShowPlaceholders === 'boolean') c.apexPage.showPlaceholders = s.apexShowPlaceholders;

  // ── On-Device page ────────────────────────────────────────
  if (!c.onDevicePage) c.onDevicePage = {};

  // Section toggles — only write if explicitly set (undefined keeps page default=true)
  if (s.odHeroImage         !== undefined) c.onDevicePage.heroImage         = s.odHeroImage;
  if (s.odPiImage           !== undefined) c.onDevicePage.piImage           = s.odPiImage;
  if (s.odHeroEnabled       !== undefined) c.onDevicePage.heroEnabled       = s.odHeroEnabled;
  if (s.odShowPlaceholders  !== undefined) c.onDevicePage.showPlaceholders  = s.odShowPlaceholders;
  if (s.odPiEnabled         !== undefined) c.onDevicePage.piEnabled         = s.odPiEnabled;
  if (s.odPlatformsEnabled  !== undefined) c.onDevicePage.platformsEnabled  = s.odPlatformsEnabled;
  if (s.odModesEnabled      !== undefined) c.onDevicePage.modesEnabled      = s.odModesEnabled;
  if (s.odPrivacyEnabled    !== undefined) c.onDevicePage.privacyEnabled    = s.odPrivacyEnabled;
  if (s.odComingSoonEnabled !== undefined) c.onDevicePage.comingSoonEnabled = s.odComingSoonEnabled;
  if (s.odCTAEnabled        !== undefined) c.onDevicePage.ctaEnabled        = s.odCTAEnabled;

  if (s.odHeroTitle)    c.onDevicePage.heroTitle    = s.odHeroTitle;
  if (s.odHeroSubtitle) c.onDevicePage.heroSubtitle = s.odHeroSubtitle;
  const odStats = [
    { value: s.odHeroStat1Value, label: s.odHeroStat1Label },
    { value: s.odHeroStat2Value, label: s.odHeroStat2Label },
    { value: s.odHeroStat3Value, label: s.odHeroStat3Label },
  ].filter(st => st.value || st.label);
  if (odStats.length) c.onDevicePage.heroStats = odStats;

  if (s.odPiDesc) c.onDevicePage.piDesc = s.odPiDesc;
  const odPiSpecs = [s.odPiSpec1, s.odPiSpec2, s.odPiSpec3, s.odPiSpec4, s.odPiSpec5].filter(Boolean);
  if (odPiSpecs.length) c.onDevicePage.piSpecs = odPiSpecs;

  const odPlatforms = [1,2,3,4].map(n => ({
    name:  s[`odPlat${n}Name`]  || null,
    desc:  s[`odPlat${n}Desc`]  || null,
    icon:  s[`odPlat${n}Icon`]  || null,
    specs: [1,2,3,4,5].map(i => s[`odPlat${n}Spec${i}`]).filter(Boolean),
  })).filter(p => p.name || p.desc);
  if (odPlatforms.length) c.onDevicePage.platforms = odPlatforms;

  const odPrivacy = [1,2,3,4].map(n => ({
    title:  s[`odPrivacy${n}Title`]  || null,
    detail: s[`odPrivacy${n}Detail`] || null,
  })).filter(p => p.title || p.detail);
  if (odPrivacy.length) c.onDevicePage.privacyPoints = odPrivacy;

  if (s.odCTABody) c.onDevicePage.ctaBody = s.odCTABody;
  const odCTA = [s.odCTACheck1, s.odCTACheck2, s.odCTACheck3, s.odCTACheck4, s.odCTACheck5].filter(Boolean);
  if (odCTA.length) c.onDevicePage.ctaChecklist = odCTA;

  // ── Cloud page ─────────────────────────────────────────────
  if (!c.cloudPage) c.cloudPage = {};
  if (s.clHeroImage !== undefined) c.cloudPage.heroImage = s.clHeroImage;

  // Section toggles
  if (s.clHeroEnabled        !== undefined) c.cloudPage.heroEnabled        = s.clHeroEnabled;
  if (s.clArchEnabled        !== undefined) c.cloudPage.archEnabled        = s.clArchEnabled;
  if (s.clApiFeaturesEnabled !== undefined) c.cloudPage.apiFeaturesEnabled = s.clApiFeaturesEnabled;
  if (s.clPlatformsEnabled   !== undefined) c.cloudPage.platformsEnabled   = s.clPlatformsEnabled;
  if (s.clInfraEnabled       !== undefined) c.cloudPage.infraEnabled       = s.clInfraEnabled;
  if (s.clComparisonEnabled  !== undefined) c.cloudPage.comparisonEnabled  = s.clComparisonEnabled;
  if (s.clCTAEnabled         !== undefined) c.cloudPage.ctaEnabled         = s.clCTAEnabled;

  if (s.clHeroTitle)    c.cloudPage.heroTitle    = s.clHeroTitle;
  if (s.clHeroSubtitle) c.cloudPage.heroSubtitle = s.clHeroSubtitle;
  const clStats = [
    { value: s.clHeroStat1Value, label: s.clHeroStat1Label },
    { value: s.clHeroStat2Value, label: s.clHeroStat2Label },
    { value: s.clHeroStat3Value, label: s.clHeroStat3Label },
  ].filter(st => st.value || st.label);
  if (clStats.length) c.cloudPage.heroStats = clStats;

  const clPlatforms = [1,2,3,4].map(n => ({
    name:  s[`clPlat${n}Name`]  || null,
    desc:  s[`clPlat${n}Desc`]  || null,
    icon:  s[`clPlat${n}Icon`]  || null,
    specs: [1,2,3,4,5].map(i => s[`clPlat${n}Spec${i}`]).filter(Boolean),
  })).filter(p => p.name || p.desc);
  if (clPlatforms.length) c.cloudPage.platforms = clPlatforms;

  const clInfra = [1,2,3,4].map(n => ({
    title:  s[`clInfra${n}Title`]  || null,
    detail: s[`clInfra${n}Detail`] || null,
  })).filter(p => p.title || p.detail);
  if (clInfra.length) c.cloudPage.infraPoints = clInfra;

  if (s.clCTABody) c.cloudPage.ctaBody = s.clCTABody;
  const clCTA = [s.clCTACheck1, s.clCTACheck2, s.clCTACheck3, s.clCTACheck4, s.clCTACheck5].filter(Boolean);
  if (clCTA.length) c.cloudPage.ctaChecklist = clCTA;

  // ── General ────────────────────────────────────────────────
  if (typeof s.footerEnabled === 'boolean') c.footer.enabled = s.footerEnabled;

  return c;
}

// ─────────────────────────────────────────────────────────────
// Public server fetch — no auth required, called on page load
// ─────────────────────────────────────────────────────────────

export async function fetchContentFromServer() {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/content`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.success && data.config && Object.keys(data.config).length > 0) {
      return data.config;
    }
  } catch (e) {
    console.warn('[config] Server unreachable, using defaults:', e.message);
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Admin-only fetch — requires Bearer token
// Called only inside AdminPanel after a successful login
// ─────────────────────────────────────────────────────────────

export async function loadAdminConfigFromServer(token) {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/admin/config`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return (data.success && data.config) ? data.config : null;
  } catch (e) {
    console.error('[config] Error fetching admin config:', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// WebSocket sync — connects once, calls onUpdate with raw
// overrides whenever the server pushes a change.
// ─────────────────────────────────────────────────────────────

export function startConfigSync(onUpdate) {
  if (typeof window === 'undefined') return () => {};

  const socket = io(getApiBaseUrl(), {
    reconnection:         true,
    reconnectionDelay:    1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 10,
  });

  socket.on('connect', () => {
    console.log('[config] WebSocket connected');
  });

  socket.on('connection_response', ({ config: serverConfig }) => {
    if (serverConfig && Object.keys(serverConfig).length > 0) {
      onUpdate(serverConfig);
    }
  });

  socket.on('config_updated', ({ config: serverConfig }) => {
    if (serverConfig) {
      console.log('[config] Live update received');
      onUpdate(serverConfig);
    }
  });

  socket.on('disconnect', () => {
    console.log('[config] WebSocket disconnected');
  });

  return () => socket.disconnect();
}